import { supabase } from '../../config/supabaseClient';
import { ENV } from '../../config/env';

// =====================================================
// TYPES & INTERFACES
// =====================================================

export interface Comment {
  id: string;
  task_id: string;
  user_id: string;
  content: string;
  created_at: string;
  updated_at: string;
  // From join
  user_name?: string;
  user_email?: string;
}

export interface CommentInput {
  task_id: string;
  content: string;
  mentions?: string[];
}

// =====================================================
// SERVICE IMPLEMENTATION
// =====================================================

const API_BASE_URL = `${ENV.API_URL}/api/project-management/comments`;

const getToken = async () => {
  const { data: { session } } = await supabase.auth.getSession();
  return session?.access_token || '';
};

export const commentsService = {
  /**
   * List comments for a task
   */
  async list(taskId: string): Promise<{ data: Comment[] | null; error: any }> {
    try {
      const token = await getToken();
      const response = await fetch(`${API_BASE_URL}?task_id=${taskId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const error = await response.json();
        return { data: null, error: error.error || 'Failed to fetch comments' };
      }

      const result = await response.json();
      return { data: result.data, error: null };
    } catch (err: any) {
      console.error('Error in commentsService.list:', err);
      return { data: null, error: err.message || 'Network error' };
    }
  },

  /**
   * Create a new comment
   */
  async create(input: CommentInput): Promise<{ data: Comment | null; error: any }> {
    try {
      const token = await getToken();
      const response = await fetch(API_BASE_URL, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(input),
      });

      if (!response.ok) {
        const error = await response.json();
        return { data: null, error: error.error || 'Failed to create comment' };
      }

      const result = await response.json();
      return { data: result.data, error: null };
    } catch (err: any) {
      console.error('Error in commentsService.create:', err);
      return { data: null, error: err.message || 'Network error' };
    }
  },

  /**
   * Delete a comment
   */
  async delete(commentId: string): Promise<{ error: any }> {
    try {
      const token = await getToken();
      const response = await fetch(`${API_BASE_URL}/${commentId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const error = await response.json();
        return { error: error.error || 'Failed to delete comment' };
      }

      return { error: null };
    } catch (err: any) {
      console.error('Error in commentsService.delete:', err);
      return { error: err.message || 'Network error' };
    }
  },
};
